import { Component, type ErrorInfo, type ReactNode } from "react";

import errorLoading from "@/assets/images/errorLoading.svg";
import logo from "@/assets/images/logo.svg";
import { CustomButton, ResourceErrorView } from "@/components/atoms";
import { COMPANY_NAME } from "@/utils/constants/defaultNames";
import { isResourceHttpError } from "@/utils/getApiErrorStatus";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  resetKey?: string;
  enableResourceView?: boolean;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: unknown;
  errorInfo?: ErrorInfo;
}

const IS_MODE_DEV = process.env.NODE_ENV === "development";

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }

  return "Unknown error";
};

const getErrorStack = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.stack;
  }

  return undefined;
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });

    if (process.env.NODE_ENV === "development") {
      console.error("Error Boundary caught an error:", error, errorInfo);
    }

    // Here you could send error to your error reporting service
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.resetKey !== prevProps.resetKey && this.state.hasError) {
      this.props.onReset?.();
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      if (
        this.props.enableResourceView &&
        isResourceHttpError(this.state.error)
      ) {
        return <ResourceErrorView error={this.state.error} />;
      }

      return (
        <div className="flex flex-col items-center justify-center my-auto w-full min-h-screen">
          <img
            src={logo}
            alt={`${COMPANY_NAME} logo`}
            data-testid="logo"
            className="w-[137px] mb-6"
          />
          <img src={errorLoading} alt="not found image" className="mb-3" />
          <p className="mb-1 font-bold">We couldn&apos;t load this page</p>
          <div className="max-w-[20%] mb-4 text-center text-sm">
            Refresh the page and try again. If this problem persists, contact{" "}
            <a href="/" target="_blank" className="mx-1 text-[#155FCC]">
              {COMPANY_NAME} support.
            </a>
          </div>
          <CustomButton onClick={this.handleReload}>Refresh</CustomButton>

          {IS_MODE_DEV && this.state.error != null && (
            <details className="mb-6 text-left">
              <summary className="cursor-pointer text-sm font-medium mb-2">
                Error Details (Development)
              </summary>
              <div className="bg-destructive/10 border border-destructive rounded p-3 text-sm">
                <div className="font-medium mb-1">Error:</div>
                <div className="text-destructive mb-2">
                  {getErrorMessage(this.state.error)}
                </div>
                <div className="font-medium mb-1">Stack:</div>
                <pre className="text-xs overflow-auto">
                  {getErrorStack(this.state.error)}
                </pre>
              </div>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
